import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useOutletContext,
  Link,
  useNavigate,
} from 'react-router';

import {
  Button,
  DataCard,
  Icon,
} from 'anf-core-react';

import ANFLink from 'anf-core-react/components/Link';

import ProductExchangeCard from '../../components/product-exchange-card/product-exchange-card';
import ExchangeDisplay from '../../components/exchange-display/exchange-display';
import ExchangeSummary from '../../components/exchange-summary/exchange-summary';
import RefundMethodDisplay from '../../components/refund-method-display/refund-method-display';
import TmntText from '../../components/tmnt-text/tmnt-text';
import CancelModal from '../../components/cancel-modal/cancel-modal';
import DropOffLocationsDisplay from '../../components/drop-off-locations-display/drop-off-locations-display';

function buildShowMapLink(selectedShippingMethod, tmntKey) {
  const showMapLinkChild = selectedShippingMethod?.children?.find((c) => !!c.showMapLink);
  if (showMapLinkChild) {
    return (
      <ANFLink href={selectedShippingMethod?.mapUrl} target="_blank">
        {showMapLinkChild.showMapLink}
      </ANFLink>
    );
  }
  return (
    <Link to={selectedShippingMethod?.mapUrl} target="_blank">
      <TmntText k={tmntKey} />
    </Link>
  );
}

export default function ReviewExchangeView() {
  const navigate = useNavigate();
  const {
    estimatedRefundCalculation,
    flow,
    orderDetail,
    selectedProducts,
    selectedProductComments,
    selectedShippingMethod,
    setPageTitle,
    setReady,
    currentPostalCode,
    selectedRefundMethodType,
    selectedRefundMethod,
  } = useOutletContext();

  const [isCancel, setIsCancel] = useState(false);

  const labelReturning = TmntText({ k: 'DRE_RETURNING', textOnly: true });
  const labelDropOffAtAnyLocation = TmntText({
    k: 'DRE_DROP_OFF_AT_ANY_TEXT_LOCATION',
    replacements: {
      0: selectedShippingMethod?.name,
    },
    textOnly: true,
  });
  const labelQRCodeLocation = TmntText({
    k: 'DRE_TEXT_DROP_OFF_QRCODE',
    replacements: {
      0: selectedShippingMethod?.name,
    },
    textOnly: true,
  });
  const labelExchanging = TmntText({ k: 'DRE_EXCHANGING', textOnly: true });
  const labelExchangeShippingAddress = TmntText({ k: 'DRE_EXCHANGE_SHIPPING_ADDRESS', textOnly: true });
  const labelExchangeShipItMyself = TmntText({ k: 'DRE_SHIP_IT_MYSELF', textOnly: true });

  useEffect(() => setReady(true), []);

  const returnItems = useMemo(() => {
    const products = selectedProducts.filter((p) => !p.exchange);
    return products.length ? products : null;
  }, [selectedProducts]);

  const exchangeItems = useMemo(() => {
    const products = selectedProducts.filter((p) => !!p.exchange);
    return products.length ? products : null;
  }, [selectedProducts]);

  // Redirects to previous step if essential data is missing
  useEffect(() => {
    if (
      !selectedProducts
      || selectedProducts.length === 0
      || !selectedShippingMethod
      // Only require refund method if there are returns
      || (returnItems && !selectedRefundMethodType)
    ) {
      navigate(-1);
    }
  }, [selectedProducts, selectedShippingMethod, selectedRefundMethodType, navigate]);

  useEffect(() => {
    if (returnItems && exchangeItems) {
      setPageTitle('DRE_REVIEW_AND_CONFIRM_RETURN_EXCHANGE');
    } else if (exchangeItems && !returnItems) {
      setPageTitle('DRE_REVIEW_AND_CONFIRM_EXCHANGE');
    } else {
      setPageTitle('DRE_REVIEW_AND_CONFIRM_RETURN');
    }
  }, [returnItems, exchangeItems]);

  const returnProducts = useMemo(() => {
    if (!returnItems) return null;

    return (
      <DataCard
        headerContent={(
          <Link data-theme="default" to={flow[0].path}>
            <TmntText k="DRE_EDIT" />
          </Link>
        )}
        headerTitle={labelReturning}
        variant="bordered"
      >
        {returnItems.map((product) => (
          <ProductExchangeCard
            key={`product-exchange-card-${product.orderItemToken}`}
            product={product}
            selectedProductComments={selectedProductComments}
            variant="bordered"
            showReturnReason
          />
        ))}
      </DataCard>
    );
  }, [flow, returnItems, labelReturning]);

  const exchangeProducts = useMemo(() => {
    if (!exchangeItems) return null;

    return (
      <DataCard
        headerContent={(
          <Link data-theme="default" to={flow[0].path}>
            <TmntText k="DRE_EDIT" />
          </Link>
        )}
        headerTitle={labelExchanging}
        variant="bordered"
      >
        {exchangeItems.map((p) => (
          <ExchangeDisplay
            key={`product-exchange-card-${p.orderItemToken}`}
            product={p}
            variant="bordered"
            selectedProductComments={selectedProductComments}
            showReturnReason
          />
        ))}
      </DataCard>
    );
  }, [flow, exchangeItems, labelExchanging]);

  const labelReturnMethod = useMemo(() => {
    if (selectedShippingMethod?.returnMethodPreference !== 'Label') {
      return null;
    }

    const dropoffLocations = (
      selectedShippingMethod?.dropoffLocationsResponse?.dropoffLocations?.slice(0, 2)
    );
    return (
      <DataCard
        headerContent={(
          <Link data-theme="default" to={flow[1].path}>
            <TmntText k="DRE_EDIT" />
          </Link>
        )}
        headerTitle={labelDropOffAtAnyLocation}
        variant="bordered"
      >
        <h4
          className="mt-1 drop-label"
          data-testid={`carrier-${selectedShippingMethod?.type}-description`}
        >
          {selectedShippingMethod?.description}
        </h4>
        <TmntText k="DRE_EMAIL_RETURN_LABEL_WITH_AMOUNT_TEXT" replacements={{ 0: selectedShippingMethod?.returnFeeFmt }} />
        {!!dropoffLocations && (
          <div className="exchange-location-selection-view-expansion-footer">
            <div className="capitalize primary-color">
              <TmntText k="CHK_LOCATIONS_NEAR" />
              &nbsp;
              <span>{`${currentPostalCode}:`}</span>
            </div>
          </div>
        )}

        {dropoffLocations?.map(
          (location, idx) => (
            <DropOffLocationsDisplay location={location} idx={idx} key={location.id} />
          ),
        )}

        <div className="link-wrapper">
          {buildShowMapLink(selectedShippingMethod, 'DRE_SEE_ALL_LOCATIONS')}
          <div className="external-link-icon">
            <Icon
              icon="external-link-anf"
              labelText="external-link-anf"
            />
          </div>
        </div>
      </DataCard>
    );
  }, [
    flow,
    selectedShippingMethod,
    labelDropOffAtAnyLocation,
    currentPostalCode,
  ]);

  const qrCodeReturnMethod = useMemo(() => {
    if (selectedShippingMethod?.returnMethodPreference !== 'QR') {
      return null;
    }

    const dropoffLocations = (
      selectedShippingMethod?.dropoffLocationsResponse?.dropoffLocations?.slice(0, 2)
    );
    return (
      <DataCard
        headerContent={(
          <Link data-theme="default" to={flow[1].path}>
            <TmntText k="DRE_EDIT" />
          </Link>
        )}
        headerTitle={labelQRCodeLocation}
        variant="bordered"
      >
        <h4
          className="mt-1 drop-label"
          data-testid={`carrier-${selectedShippingMethod?.type}-description`}
        >
          {selectedShippingMethod?.description}
        </h4>
        <TmntText k="DRE_RECEIVE_QRCODE_FEE" replacements={{ 0: selectedShippingMethod?.returnFeeFmt }} />

        {!!dropoffLocations && (
          <div className="exchange-location-selection-view-expansion-footer">
            <div className="capitalize primary-color">
              <TmntText k="DRE_QRCODE_LOCATIONS_NEAR" replacements={{ 0: currentPostalCode }} />
            </div>
          </div>
        )}
        {dropoffLocations?.map(
          (location, idx) => (
            <DropOffLocationsDisplay location={location} idx={idx} key={location.id} />
          ),
        )}
        <div className="link-wrapper">
          {selectedShippingMethod?.eligibilityNoticeText
            ? <span>{selectedShippingMethod?.eligibilityNoticeText}</span>
            : buildShowMapLink(selectedShippingMethod, 'DRE_SEARCH_ALL_QRCODE_LOCATIONS')}
          {!selectedShippingMethod?.eligibilityNoticeText && (
          <div className="external-link-icon">
            <Icon
              icon="external-link-anf"
              labelText="external-link-anf"
            />
          </div>
          )}
        </div>
      </DataCard>
    );
  }, [
    flow,
    selectedShippingMethod,
    labelQRCodeLocation,
    currentPostalCode,
  ]);


  const returnInStoreReturnMethod = useMemo(() => {
    if (selectedShippingMethod?.returnMethodPreference !== 'ReturnInStore') {
      return null;
    }

    const dropoffLocations = (
      selectedShippingMethod?.dropoffLocationsResponse?.dropoffLocations?.slice(0, 2)
    );

    const storesNearText = (
      selectedShippingMethod
        ?.children?.find((c) => !!c.postalCode)
        ?.postalCode
        ?.replace('{0}', currentPostalCode)
    );

    const searchAllStoresText = (
      selectedShippingMethod
        ?.children
        ?.find((c) => !!c.dropoffLocationsLink)
        ?.dropoffLocationsLink
    );

    return (
      <DataCard
        headerContent={(
          <Link data-theme="default" to={flow[1].path}>
            <TmntText k="DRE_EDIT" />
          </Link>
        )}
        headerTitle={selectedShippingMethod?.header}
        variant="bordered"
      >
        <div
          className="mt-1"
          data-testid={`carrier-${selectedShippingMethod?.type}-description`}
        >
          {selectedShippingMethod?.description}
        </div>

        {!!dropoffLocations?.length > 0 && (
          <div className="exchange-location-selection-view-expansion-footer">
            <div className="capitalize">
              {storesNearText}
            </div>
          </div>
        )}
        {dropoffLocations?.map(
          (location, idx) => (
            <DropOffLocationsDisplay location={location} idx={idx} key={location.id} />
          ),
        )}
        <div className="link-wrapper">
          <ANFLink href={selectedShippingMethod?.mapUrl} target="_blank">
            {searchAllStoresText}
          </ANFLink>
          <div className="external-link-icon">
            <Icon
              icon="external-link-anf"
              labelText="external-link-anf"
            />
          </div>
        </div>
      </DataCard>
    );
  }, [
    flow,
    selectedShippingMethod,
    currentPostalCode,
  ]);

  const shipMySelfReturnMethod = useMemo(() => {
    if (selectedShippingMethod?.returnMethodPreference !== 'ShipMySelf') {
      return null;
    }

    return (
      <DataCard
        headerContent={(
          <Link data-theme="default" to={flow[1].path}>
            <TmntText k="DRE_EDIT" />
          </Link>
        )}
        headerTitle={labelExchangeShipItMyself}
        variant="bordered"
      >
        <TmntText k="DRE_SHIP_INVOICE_PRINT_DETAILS" />
        <br />
        <br />
        <TmntText k="DRE_SHIP_INVOICE_ORDER_CONFIRMATION_DETAILS" />
      </DataCard>
    );
  }, [
    flow,
    labelExchangeShipItMyself,
  ]);

  const refundMethod = useMemo(() => {
    if (exchangeItems && !returnItems) return null;
    if (!selectedRefundMethod) return null;

    const { orderDetails } = orderDetail.originalOrder;
    const { payments, email } = orderDetails;
    const {
      titleText,
      receiptHeaderText,
      receiptTitleText,
      receiptBodyText,
    } = selectedRefundMethod;

    const canDisplayReceipt = receiptHeaderText && receiptTitleText && receiptBodyText;

    return (
      <div className="refund-method" data-testid="refund-method">
        <DataCard
          headerContent={(
            <Link data-theme="default" to={flow[2].path}>
              <TmntText k="DRE_EDIT" />
            </Link>
          )}
          headerTitle={titleText}
          variant="bordered"
        >
          <div className="card-wrapper">
            {(selectedRefundMethodType === 'EGC' || selectedRefundMethodType === 'MC') && (
              <RefundMethodDisplay
                payment={{ paymentName: 'GIFT_CARD' }}
                email={email}
                key={'egc-mc'}
                refundMethod={selectedRefundMethod}
              />
            )}
            {selectedRefundMethodType !== 'EGC' && selectedRefundMethodType !== 'MC' && payments.map((p) => (
              <RefundMethodDisplay
                payment={p}
                email={email}
                key={p.cardNumber}
                refundMethod={selectedRefundMethod}
              />
            ))}
          </div> 
        </DataCard>
        {canDisplayReceipt && (
          <DataCard
            headerTitle={receiptHeaderText}
            variant="bordered"
          >
            <div
              className="card-wrapper"
              data-testid="refund-method-receipt"
            >
            <div className="card-body">
              <div className="card-body-text">
                <div className="receipt-title">{receiptTitleText}</div>
                <div className="receipt-body">{receiptBodyText}</div>
              </div>
            </div>
          </div>
        </DataCard>
        )}
      </div>
    );
  }, [
    flow,
    orderDetail,
    selectedRefundMethodType,
    exchangeItems,
    returnItems,
    selectedRefundMethod
  ]);

  const exchangeInformation = useMemo(() => {
    if (!exchangeItems) return null;

    const {
      firstName,
      lastName,
      address1,
      address2,
      city,
      state,
      postalCode,
    } = orderDetail.originalOrder.orderDetails.shippingAddress;

    return (
      <DataCard
        headerContent={(<span />)}
        headerTitle={labelExchangeShippingAddress}
        variant="bordered"
      >
        <div>{`${firstName}, ${lastName}`}</div>
        <div>{`${address1} ${address2}`}</div>
        <div>{`${city}, ${state}, ${postalCode}`}</div>
        <h5 className="mt-2">Standard Shipping</h5>
        <p>
          <TmntText k="DRE_EXCHANGE_ARRIVE_DETAILS" />
        </p>
      </DataCard>
    );
  }, [
    orderDetail,
    exchangeItems,
    returnItems,
    labelExchangeShippingAddress,
  ]);

  const returnMethod = useMemo(() => {
    // NOTE: we'll be creating a new component soon to encapsulate generating
    // all the return methods for the review screen 
    if (selectedShippingMethod?.returnMethodPreference === 'ReturnInStore') {
      return returnInStoreReturnMethod;
    }
    if ( selectedShippingMethod?.returnMethodPreference === 'QR') {
      return qrCodeReturnMethod;
    }
    if (selectedShippingMethod?.type === 'shipmyself') {
      return shipMySelfReturnMethod;
    }
    return labelReturnMethod;
  }, [
    selectedShippingMethod,
    qrCodeReturnMethod,
    labelReturnMethod,
    shipMySelfReturnMethod,
    returnInStoreReturnMethod,
  ]);

  return (
    <div className="review-exchange-view" data-testid="review-exchange-view">
      {returnProducts}
      {exchangeProducts}
      {returnMethod}
      {refundMethod}
      {exchangeInformation}
      <ExchangeSummary calculation={estimatedRefundCalculation} className="mobile-only" />
      <div className="review-exchange-view-cancel mobile-only">
        <Button
          variant="tertiary-light"
          isFullWidth
          onClick={() => setIsCancel(true)}
        >
          <TmntText k="GLB_CANCEL" />
        </Button>
      </div>
      <CancelModal
        isOpen={isCancel}
        setOpen={setIsCancel}
        handleSubmit={() => window.location.reload()}
      />
    </div>
  );
}
